import { useDispatch, useSelector } from "react-redux";
import {
  Composite,
  FormState,
  StoredAdviceCartEntity,
} from "./priceCalculationInterfaces";
import {
  getStoredAdviceCartStart,
  initializePriceCalculationFormState,
} from "./priceCalculationSlice";

import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { useExistingCart } from "Components/Features/CheckPrice/hooks/checkPriceHooks";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { usePartnerId } from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { PriceCalculationEntry } from "Store/priceCalculation/priceCalculationInterfaces";
import { ReduxState } from "Store/rootReducer";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { isDefined } from "Utils/functions";
import { hasProperty } from "Utils/generalUtils";
import { isAkademikerneTheme } from "Utils/partnerUtils";
import { useEffect } from "react";
import { useStoredAdviceCartSelector } from "./priceCalculationSelectors";

export const useFormState = <T = FormState>(
  stepId: string,
  initialFormState: Partial<T> = {},
): Partial<T> => {
  const dispatch = useDispatch();

  const storedFormState = useSelector(
    (state: ReduxState) =>
      state.priceCalculation.priceCalculationFormStates[stepId],
  ) as T | Partial<T> | undefined;

  useEffect(() => {
    if (!storedFormState) {
      dispatch(
        initializePriceCalculationFormState({
          stepId,
          formState: initialFormState,
        }),
      );
    }
  }, [initialFormState]);

  return !storedFormState ? initialFormState : storedFormState;
};

export const useCompletedFormState = <T = FormState>(stepId: string): T => {
  const formState = useSelector(
    (state: ReduxState) =>
      state.priceCalculation.priceCalculationFormStates[stepId],
  ) as T | undefined;

  if (!isDefined(formState)) {
    throw new Error("Required step has not been completed");
  }

  return formState;
};

export const useCoveragePriceCalculations = (): CoveragePriceCalculation[] => {
  const {
    priceCalculation: {
      priceCalculationResult: { current },
    },
  } = useSelector((state: ReduxState) => state);

  if (current !== null && hasProperty(current, "composites")) {
    return mapCompositesToCoveragePriceCalculation(current.composites);
  }

  const calculations = Object.entries(
    current ?? {},
  ) as CoveragePriceCalculation[];
  return calculations.sort(
    (a, b) => a[1].prices[0].yearlyPrice - b[1].prices[0].yearlyPrice,
  );
};

export const mapCompositesToCoveragePriceCalculation = (
  composites: Record<string, Composite>,
): CoveragePriceCalculation[] => {
  const compositions: string[] = [];

  return Object.entries(composites).map((composite) => {
    compositions.push(composite[0]);
    return [
      composite[0] as InsuranceCoverage,
      {
        prices: [composite[1].prices[0]],
        compositions: compositions,
      } as PriceCalculationEntry,
    ];
  });
};

export const useStoredAdviceCart = (): StoredAdviceCartEntity => {
  const cartEntity = useExistingCart();
  const cartId = cartEntity.current?.cartId;
  const dispatch = useDispatch();
  const partnerId = usePartnerId();
  const userIdentification = useUserIdentificationSelector();
  const storedAdviceCartEntity = useStoredAdviceCartSelector();
  const { current, isLoading } = storedAdviceCartEntity;
  useEffect(() => {
    if (
      isDefined(cartId) &&
      !isDefined(current) &&
      !isLoading &&
      isDefined(userIdentification)
    ) {
      dispatch(
        getStoredAdviceCartStart({
          cartId,
          isCollectiveAdvice: isAkademikerneTheme(partnerId as Partner),
        }),
      );
    }
  }, [cartId, partnerId, userIdentification]);
  return storedAdviceCartEntity;
};
